// src/utils/helpers.js
const { v4: uuidv4 } = require('uuid');


const isRecent = (timestamp) => {
  const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
  return new Date(timestamp) > fiveMinutesAgo;
};


// Format duration in "0m 0s" format
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};


// for voicemail records
export const getDurationDisplay = (record) => {
  const { duration, timestamp, summary } = record;

  // Rule 1: "hangup" string trumps everything
  if (duration === 'hangup') return 'hangup';

  // Rule 2: Empty string for recent null/undefined duration
  if (!duration && isRecent(timestamp)) return '';

  // Rule 3 & 5: "hangup" for old recordings with short/null duration
  if ((!duration || parseInt(duration) < 2) && !isRecent(timestamp)) return 'hangup';

  // Rule 6: Check for "RECORDING" in summary
  if (!isRecent(timestamp) && summary?.includes('RECORDING')) return 'hangup';

  // Rule 4: Format valid durations
  if (duration && parseInt(duration) >= 2) return formatDuration(parseInt(duration));

  return '';
};


// format duration value 
export function prettySeconds(seconds) {
    if (typeof seconds === 'string') {
        seconds = parseInt(seconds);
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (!seconds) {
      return "hangup"
    }
    
    return `${minutes}m ${remainingSeconds}s`;
}


export function prettyPhone(phoneNumber) {
  if (!phoneNumber) return ''; // Add this single line to handle undefined/null
  
  // Remove any non-digit characters and ensure we're working with the last 10 digits
  const cleaned = phoneNumber.replace(/\D/g, '').slice(-10);
  
  // Check if we have a valid 10-digit number
  if (cleaned.length !== 10) {
    return phoneNumber; // Return original if not valid
  }
  
  // Format the number
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
}


// plus one phone number format
// eslint-disable-next-line
function plusOne(phoneNumber) {
    // Strip everything except digits
    const digits = phoneNumber.replace(/\D/g, '');
    
    // Handle empty string
    if (!digits) {
        return '';
    }
    
    // Remove leading 1 if it exists
    const normalizedDigits = digits.startsWith('1') ? digits.slice(1) : digits;
    
    // Add +1 prefix
    return `+1${normalizedDigits}`;
}


// Get UUID
export function getUUID() {
    return uuidv4(); // Always generates a new UUID
}



// localized system prompts also need to be copioed to helpers in portal
export const systemPrompts = {

  'weDidNotHearThat': {
    'en-US': 'We did not hear that.',
    'es-MX': 'No escuchamos lo que dijo.',
    'ru-RU': 'Мы этого не услышали.'
  },

  'thankYouPleaseWaitAMoment': {
    'en-US': 'Thank you. Please wait a moment.',
    'es-MX': 'Gracias. Por favor, espere un momento.',
    'ru-RU': 'Спасибо. Пожалуйста, подождите немного.'
  },

  'pleaseWaitAMoment': {
    'en-US': 'Please wait a moment.',
    'es-MX': 'Por favor, espere un momento.',
    'ru-RU': 'Пожалуйста, подождите немного.'
  },

  'weAreBack': {
    'en-US': 'We are back.',
    'es-MX': 'Hemos regresado.',
    'ru-RU': 'Мы вернулись.'
  },

  // this requires a special translation, do not auto-translate this
  'pleaseSayLanguage': {
    'en-US': 'Please say "one" for English.',
    'es-MX': 'Por favor, diga "dos" para español.',
    'ru-RU': 'Пожалуйста, скажите "три"" для русского.'
  },

  'youSaid': {
    'en-US': 'You said, ${speechToSay}. Is this correct? Say yes or no.', 
    'es-MX': 'Dijiste, ${speechToSay}. ¿Es correcto? Diga sí o no.',
    'ru-RU': 'Вы сказали, ${speechToSay}. Верно? Скажите да или нет.'
  },

  'talkFaster': {
    'en-US': 'If you are having trouble with numbers, try speaking faster instead of slower, and try speaking a little softer.',
    'es-MX': "Si tiene problemas con los números, intente hablar más rápido en lugar de más lento, y trate de hablar un poco más suave.",
    'ru-RU': 'Если у вас возникают трудности с числами, попробуйте говорить быстрее, а не медленнее, и говорите немного тише.'
  },

  'skipped': {
    'en-US': '(skipped)',
    'es-MX': '(omitido)',
    'ru-RU': '(пропущено)'
  },

  'agentWouldToSpeak': {
    'en-US': 'An agent would like to speak with you now. This call is being recorded.',
    'es-MX': 'Un agente quiere hablar contigo ahora. Esta llamada está siendo grabada.',
    'ru-RU': 'Сейчас с вами хочет поговорить агент. Этот звонок записывается.'
  },

  'callRecordError': {
    'en-US': 'There was a record action database error. Call record not found.',
    'es-MX': 'Hubo un error en la base de datos. Registro de llamada no encontrado.',
    'ru-RU': 'Произошла ошибка базы данных. Запись звонка не найдена.'
  },

  'recordAtTone': {
    'en-US': 'Please record your message at the tone. Hang up when you are done.',
    'es-MX': 'Por favor, grabe su mensaje después del tono. Cuelgue cuando termine.',
    'ru-RU': 'Пожалуйста, начните запись сообщения после сигнала. Положите трубку, когда закончите.'
  },

  'detaineeCallbackDefaultMessage': {
    'en-US': 'We see that you called previously. No specific message has been left for you. But please leave us a message with any additional details on your case.',
    'es-MX': 'Vemos que has llamado anteriormente. No se ha dejado ningún mensaje específico para ti. Pero por favor déjanos un mensaje con cualquier detalle adicional sobre tu caso.',
    'ru-RU': 'Мы видим, что вы звонили ранее. Для вас не было оставлено конкретного сообщения. Но пожалуйста, оставьте нам сообщение с любыми дополнительными деталями по вашему делу.'
  },

  'forwardingNow': {
    'en-US': 'We are forwarding you to an agent now.',
    'es-MX': 'Ahora lo estamos transfiriendo con un agente.',
    'ru-RU': 'Сейчас мы переводим вас на оператора.'
  },

  'agentHangup': {
    'en-US': 'The agent has hung up or disconnected. Thank you for your call.',
    'es-MX': 'El agente ha colgado o se ha desconectado. Gracias por su llamada.',
    'ru-RU': 'Оператор завершил звонок или отключился. Спасибо за ваш звонок.'
  },

  'detaineeHangup': {
    'en-US': 'The detainee has hung up or disconnected. Thank you for your call.',
    'es-MX': 'La persona detenida ha colgado o se ha desconectado. Gracias por su llamada.',
    'ru-RU': 'Задержанный завершил звонок или отключился. Спасибо за ваш звонок.'
  }

}

